import React, { useContext, useRef, useState } from 'react';
import {
  AvDateRangePicker,
  AvDialog,
  FeatureFlags,
  FilterStyledPopper,
  getDateFromFilter,
  getStringConditionFromDatePicker,
  SelectTrigger,
  useClickOutside,
  ValueWithPreset,
} from '@fabric-ui/components';
import { TimeBucket } from '@fabric-ui/components/Table';
import { flex } from '@fabric-ui/theme';
import { Box, Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import getNewRequestParams from '@utils/getNewRequestParams';
import { patchHistoricalQueryTimeRange } from '@views/CustomDashboards/patchHistoricalQueryTimeRange';
import { useAvContext } from '../../../../context/AvContextProvider';
import { FieldType as MappingFieldType } from '../../../Sources/Mapping/types';
import { Orientation } from '../../constants';
import { CustomDashboardContext } from '../../CustomDashboardContext';
import { hasTopKFilter, removeTopKFilters } from '../../EditCustomDashboard/Tabs/WidgetFilters/utils';
import { hasTimeBucket } from '../../EditCustomDashboard/TimeBucketBreakdown/utils';
import { AllTypeWidgets, HistoricalFilterMode, TileSubType, WidgetCategory } from '../../types';
import { FieldType } from '../../types/types';
import { newDefaultTimeRange, TimeframeOptions } from './constants';
import { formatTimeRange } from './utils';

const TimeFilter = ({
  orientation = Orientation.Horizontal,
  isMultiBreakdown,
}: {
  orientation?: Orientation;
  isMultiBreakdown?: boolean;
}) => {
  const { newWidget, setNewWidget } = useContext(CustomDashboardContext);
  const {
    measurements,
    featureFlags,
    accountEntities: { fieldMap },
  } = useAvContext();

  const [timeframe, setTimeframe] = useState(
    newWidget.requests[0].timeRange ? TimeframeOptions.historic.value : TimeframeOptions.current.value
  );
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [switchTimeFrameDialogIsOpen, setSwitchTimeFrameDialogIsOpen] = useState(false);

  const dateRangeInputRef = useRef(null);
  const datePickerRef = useRef(null);

  const dateObject = getDateFromFilter(newWidget.requests[0].timeRange ?? {});

  const isSameCalendarDayRange = (() => {
    const between = newWidget.requests[0].timeRange?.between;
    const [from, to] = between?.values ?? [];
    if (!from || !to) {
      return false;
    }
    const fromDate = new Date(from);
    const toDate = new Date(to);
    if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) {
      return false;
    }
    return (
      fromDate.getFullYear() === toDate.getFullYear() &&
      fromDate.getMonth() === toDate.getMonth() &&
      fromDate.getDate() === toDate.getDate()
    );
  })();

  const triggerProps = {
    ref: dateRangeInputRef,
    onClick: () => setIsDatePickerOpen(true),
    open: isDatePickerOpen,
    chipCount: undefined,
    label: formatTimeRange(newWidget.requests[0].timeRange ?? {}),
    size: 'xSmall',
    sx: {
      width: '100%',
      border: `1px solid ${isSameCalendarDayRange ? 'var(--color-border-status-danger-primary)' : 'var(--color-border-interactive-secondary-default)'}`,
      backgroundColor: 'var(--color-surface-field-default)',
      fontWeight: 400,
      '&:hover': {
        backgroundColor: 'var(--color-surface-field-hover)',
      },
      '&.Mui-focusVisible': {
        backgroundColor: 'var(--color-surface-field-default)',
        borderColor: 'var(--color-border-interactive-primary-default)',
      },
      padding: '2px 10px',
      fontSize: 12,
    },
  } as const;

  const dialogOptions = {
    [TimeframeOptions.current.value]: {
      title: 'Switch to historical view?',
      text: 'You are about to switch this chart to historical analytics, this will enable date breakdown and may remove some of the existing measurements or dimensions currently selected.',
      onClick: initTimeRangeAndTimeBucketToWidget,
    },
    [TimeframeOptions.historic.value]: {
      title: 'Switch to current view?',
      text: 'You are about to switch this chart to current analytics, this will remove date breakdown and may remove some of the existing measurements currently selected.',
      onClick: resetTimeRangeAndTimeBucket,
    },
  };

  const handleClose = () => {
    setIsDatePickerOpen(false);
  };

  useClickOutside(datePickerRef, handleClose);

  const onApply = ({ value, preset }: ValueWithPreset) => {
    const timeRange = getStringConditionFromDatePicker({ value, preset }).dateCondition;

    setNewWidget({
      ...newWidget,
      requests: [
        {
          ...newWidget.requests[0],
          timeRange: patchHistoricalQueryTimeRange(timeRange),
        },
      ],
    } as AllTypeWidgets);
  };

  const onToggleButtonChange = () => {
    if (newWidget.requests[0].select.metrics.length || newWidget.requests[0].select.dims.length) {
      setSwitchTimeFrameDialogIsOpen(true);
    } else {
      dialogOptions[timeframe].onClick();
    }
  };

  function initTimeRangeAndTimeBucketToWidget() {
    // Filter out date/datetime dimensions from current widget before calling getNewRequestParams
    const filteredCurrentDims = newWidget.requests[0].select.dims.filter(
      dim => ![MappingFieldType.DateTime, MappingFieldType.Date].includes(fieldMap[dim.name]?.type as string)
    );

    // Filter metrics to only those with timeExpression
    const filteredCurrentMetrics = newWidget.requests[0].select.metrics.filter(
      metric => measurements.asObject[metric.name]?.timeExpression
    );

    // Create a pre-filtered widget to pass to getNewRequestParams
    const preFilteredWidget = {
      ...newWidget,
      requests: [
        {
          ...newWidget.requests[0],
          select: {
            ...newWidget.requests[0].select,
            dims: filteredCurrentDims,
            metrics: filteredCurrentMetrics,
          },
        },
      ],
    } as AllTypeWidgets;

    const { newSelect, newGroupBy, newSortBy, newDrillDownHierarchy, newTop } = getNewRequestParams({
      fields: isMultiBreakdown ? [] : [TimeBucket.BUCKET_WEEK],
      fieldCategory: FieldType.Dimensions,
      widget: preFilteredWidget,
      isHistoricalAnalyticsFeatureFlag: featureFlags[FeatureFlags.HistoricalAnalytics],
    });

    const timeRange = getStringConditionFromDatePicker(newDefaultTimeRange).dateCondition;
    const currentFilter = newWidget.requests[0].filter;

    // Remove topK filters when switching to historical
    const filterWithoutTopK = currentFilter && hasTopKFilter(currentFilter) ? removeTopKFilters(currentFilter) : currentFilter;

    setNewWidget({
      ...newWidget,
      historicalFilterMode: HistoricalFilterMode.CURRENT,
      requests: [
        {
          ...newWidget.requests[0],
          select: newSelect,
          groupBy: newGroupBy,
          sorting: newSortBy,
          top: newTop,
          timeRange,
          filter: filterWithoutTopK,
        },
      ],
      drillDownHierarchy: newDrillDownHierarchy,
      definition: {
        ...newWidget.definition,
        ...(newWidget.category === WidgetCategory.Table && {
          custom: {
            ...newWidget.definition.custom,
            crossTableView: newWidget.definition.custom.crossTableView ? newSelect.dims.length === 1 : false,
          },
        }),
      },
    } as AllTypeWidgets);

    setTimeframe(TimeframeOptions.historic.value);

    setSwitchTimeFrameDialogIsOpen(false);
  }

  function resetTimeRangeAndTimeBucket() {
    const { newSelect, newGroupBy, newSortBy, newDistinct, newTop } = getNewRequestParams({
      fields: newWidget.requests[0].select.dims.filter(({ name }) => hasTimeBucket(name)).map(({ name }) => name),
      fieldCategory: FieldType.Dimensions,
      widget: newWidget,
      isHistoricalAnalyticsFeatureFlag: featureFlags[FeatureFlags.HistoricalAnalytics],
    });

    const currentFilteredMetrics = newSelect.metrics.filter(
      selectedMetric => measurements.visible.find(metric => selectedMetric.name === metric.systemName)?.expression
    );
    const currentFilteredDims = newSelect.dims.filter(({ name }) => !hasTimeBucket(name));

    const currentFilteredSortBy = newSortBy.filter(sort => currentFilteredMetrics.find(metric => sort.name === metric.name));

    const { timeRange, ...rest } = newWidget.requests[0];

    setNewWidget({
      ...newWidget,
      historicalFilterMode: undefined,
      requests: [
        {
          ...rest,
          select: { ...newSelect, metrics: currentFilteredMetrics, dims: currentFilteredDims },
          groupBy: newGroupBy,
          sorting: currentFilteredSortBy,
          distinct: newDistinct,
          top: newTop,
        },
      ],
    } as AllTypeWidgets);

    setTimeframe(TimeframeOptions.current.value);

    setSwitchTimeFrameDialogIsOpen(false);
  }

  const dialogFooter = (
    <Box sx={{ ...flex.justifyEndCenter, gap: 2 }}>
      <Button onClick={() => setSwitchTimeFrameDialogIsOpen(false)}>Cancel</Button>
      <Button variant="contained" onClick={dialogOptions[timeframe].onClick}>
        Continue
      </Button>
    </Box>
  );

  return (
    <Box sx={{ ...flex.col, gap: orientation === Orientation.Horizontal ? 2 : 1.5 }}>
      <AvDialog
        titleVariant="warning"
        title={dialogOptions[timeframe].title}
        open={switchTimeFrameDialogIsOpen}
        onClose={() => setSwitchTimeFrameDialogIsOpen(false)}
        footer={dialogFooter}>
        <div>{dialogOptions[timeframe].text}</div>
      </AvDialog>

      <Box
        sx={{
          ...(orientation === Orientation.Vertical ? flex.colItemsStart : flex.justifyBetweenCenter),
          gap: orientation === Orientation.Vertical ? 0.5 : 0,
        }}>
        <Box sx={{ fontSize: 12, color: 'var(--color-content-base-primary)' }}>Timeframe</Box>
        <ToggleButtonGroup
          color="white"
          size="xSmall"
          sx={{ '.MuiButtonBase-root.MuiToggleButton-white': { px: '10px', gap: '4px' } }}
          exclusive
          value={timeframe}
          onChange={onToggleButtonChange}>
          <ToggleButton value={TimeframeOptions.current.value} disabled={newWidget.type === TileSubType.Trend}>
            {TimeframeOptions.current.icon}
            {TimeframeOptions.current.value}
          </ToggleButton>
          <ToggleButton value={TimeframeOptions.historic.value}>
            {TimeframeOptions.historic.icon}
            {TimeframeOptions.historic.value}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {timeframe === TimeframeOptions.historic.value && (
        <div>
          <Box sx={{ fontSize: 12, color: 'var(--color-content-base-primary)', mb: orientation === 'horizontal' ? '2px' : 0.5 }}>
            Date range
          </Box>
          <SelectTrigger {...triggerProps} />
          {isSameCalendarDayRange && (
            <Box sx={{ fontSize: 12, fontWeight: 500, color: 'var(--color-content-status-danger-secondary)', mt: 0.5 }} role="alert">
              Start and end dates must be different
            </Box>
          )}
          <FilterStyledPopper
            open={isDatePickerOpen}
            anchorEl={dateRangeInputRef.current}
            placement="bottom-start"
            sx={{ minWidth: '400px', width: 'fit-content', padding: 0 }}>
            <Box ref={datePickerRef}>
              <AvDateRangePicker
                value={dateObject.value}
                activePresetValue={dateObject.preset}
                onChange={onApply}
                onClose={handleClose}
                showAsCalendar
                isRange
                showRelativeOptions
                allowFutureDates={false}
                isFilterExpression
                showOptions={false}
                isEditableInput={false}
              />
            </Box>
          </FilterStyledPopper>
        </div>
      )}
    </Box>
  );
};

export default TimeFilter;
